import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Modal } from "@vds/modals";
import { Tooltip } from "@mui/material";
import { Body } from "@vds/typography";
import { Icon } from "@vds/icons";
import { Loader } from "@vds/loaders";
import moment from "moment";
import SlickImageCarousel from "../../global/components/imageCarousel/SlickImageCarousel";
import { Button, ZoomButtonsBox } from "../../global/components/floorPlan/styles";
import BoundingBoxOverlay from "./BoundingBoxOverlay";
import { ImageDownloadMedia } from "../../model/mediaService";
import { saveHsEntityImageMapping } from "../../store/actions";
import { FORENSIC_IMAGE_DOWNLOAD_IDENTIFIER, LAZY_IMAGE_MESSAGES, SEARCH_STATUS_PAYLOAD_UPLOADING } from "./SearchConstants";

const NON_TERMINAL_STATUSES = [undefined, SEARCH_STATUS_PAYLOAD_UPLOADING];

// ResizeObserver on <img> tracks the actual rendered size so BoundingBoxOverlay
// recalculates getContainRect proportionally whenever the stage height changes.
const LightboxSlide = ({ entry, registerTransform, onRefresh, isBeingRetried }) => {
  const dispatch = useDispatch();
  const imageReducer = useSelector((state) => state.imagesReducer);
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);

  const cached = imageReducer.hsEntityImages.find((img) => img.id === entry?.imageId);

  useEffect(() => {
    let mounted = true;
    if (cached?.imageUrl) {
      setImageUrl(cached.imageUrl); setLoading(false); setFailed(false); return;
    }
    if (entry?.status === "failed" && !isBeingRetried) { setFailed(true); setLoading(false); return; }
    if (entry?.status === "failed" && isBeingRetried) { setLoading(true); setFailed(false); return; }
    if (NON_TERMINAL_STATUSES.includes(entry?.status) || !entry?.imageId) {
      setLoading(true); setFailed(false); return;
    }
    setLoading(true); setFailed(false);
    ImageDownloadMedia(entry.imageId, activeSession, null, FORENSIC_IMAGE_DOWNLOAD_IDENTIFIER)
      .then((url) => {
        if (!mounted) return;
        if (!url) { setFailed(true); return; }
        dispatch(saveHsEntityImageMapping(entry.imageId, url, entry.timestamp));
        setImageUrl(url);
      })
      .catch(() => { if (mounted) setFailed(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.imageId, entry?.status, cached?.imageUrl, isBeingRetried]);

  useEffect(() => {
    if (!imgRef.current || !imageUrl || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([obs]) => {
      const { width, height } = obs.contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [imageUrl]);

  return (
    <div className="hs-lightbox-slide">
      <div className={`hs-lightbox-stage${imageUrl ? "" : " hs-lightbox-stage--placeholder"}`}>
        <TransformWrapper ref={registerTransform} initialScale={1} minScale={1} maxScale={5}>
          <TransformComponent wrapperClass="hs-lightbox-tc-wrapper" contentClass="hs-lightbox-tc-content">
            <div className="hs-lightbox-img-box">
              {loading && (
                <div className="hs-lightbox-state">
                  <Body><span className="hs-loading-text">{LAZY_IMAGE_MESSAGES.FETCHING_SNAPSHOT}</span></Body>
                </div>
              )}
              {failed && !loading && (
                <div className="hs-lightbox-state hs-lightbox-state--failed">
                  {onRefresh ? (
                    <div
                      className="hs-lightbox-refresh-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRefresh();
                      }}
                      title="Retry"
                      style={{ cursor: "pointer" }}
                    >
                      <Icon name="sync" size="small" color="#000000" />
                    </div>
                  ) : (
                    <Icon name="error" size="small" color="#6f7171" />
                  )}
                  <Body>{LAZY_IMAGE_MESSAGES.FETCH_FAILED}</Body>
                </div>
              )}
              {imageUrl && (
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Search result"
                  className="hs-lightbox-img"
                  onLoad={(e) =>
                    setNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight })
                  }
                />
              )}
              {imageUrl && entry?.boundingBox && (
                <BoundingBoxOverlay
                  boundingBox={entry.boundingBox}
                  containerSize={containerSize}
                  naturalSize={naturalSize}
                />
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>
    </div>
  );
};

const SearchResultLightbox = ({ opened, entries, initialIndex, onClose, siteReducerSites, deviceListItem, onRefresh, pendingImageKeysRef }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const transformApis = useRef({});

  useEffect(() => {
    if (opened) setCurrentIndex(initialIndex);
  }, [opened, initialIndex]);

  const zoomCurrent = (direction) => {
    const api = transformApis.current[currentIndex];
    if (!api) return;
    direction === "in" ? api.zoomIn(0.5) : api.zoomOut(0.5);
  };

  const slides = entries.map((entry, idx) => {
    const isBeingRetried = entry?.status === "failed" && pendingImageKeysRef?.current?.has?.(`${entry.fovId}:${entry.imageIndex}`);
    return (
      <LightboxSlide
        key={`${entry.imageId}-${idx}`}
        entry={entry}
        registerTransform={(api) => { transformApis.current[idx] = api; }}
        onRefresh={onRefresh}
        isBeingRetried={isBeingRetried}
      />
    );
  });

  const activeEntry = entries[currentIndex];
  const activeFov = deviceListItem.fovList.find((d) => d.id === activeEntry?.fovId);
  const activeCamera = deviceListItem.cameraList.find((c) => c.id === activeFov?.parentdeviceid);
  const activeLocation = activeCamera?.customdata?.location;
  const activeLocationParts = [
    activeLocation?.subarea?.name && activeLocation.subarea.name !== 'NA' ? activeLocation.subarea.name : null,
    activeLocation?.area?.name || null,
    activeLocation?.floor?.name || null,
    activeLocation?.site?.name || null,
  ].filter(Boolean);
  const activeLocationText = activeLocationParts.join(", ");

  return (
    <Modal
      opened={opened}
      onOpenedChange={(isOpened) => { if (!isOpened) onClose(); }}
      width="1100px"
      maxWidth="94vw"
      maxHeight="90vh"
      zIndex={2000}
      className="hs-lightbox-modal"
      data-testid="hs-image-lightbox"
    >
      {opened && (
        <>
          <div className="hs-lightbox-toolbar">
            <span className="hs-lightbox-counter">{currentIndex + 1} / {entries.length}</span>
            <ZoomButtonsBox>
              <Button onClick={() => zoomCurrent("in")} aria-label="Zoom in">
                <Icon name="zoom-in" surface="light" size="large" />
              </Button>
              <Button onClick={() => zoomCurrent("out")} aria-label="Zoom out">
                <Icon name="zoom-out" surface="light" size="large" />
              </Button>
            </ZoomButtonsBox>
          </div>
          <div className="hs-lightbox-carousel">
            <SlickImageCarousel
              key={initialIndex}
              items={slides}
              currentIndex={initialIndex}
              onChange={setCurrentIndex}
              arrowOffset={40}
              arrowSize={36}
              settings={{ dots: false, infinite: false, speed: 300, initialSlide: initialIndex }}
            />
          </div>
          <div className="hs-lightbox-meta">
            <div className="hs-lightbox-meta-row">
              <div className="hs-thumb-stream-body">
                <strong className="hs-thumb-stream-label">Stream:</strong>
                <Tooltip title={activeFov?.name || "Unknown Stream"} placement="top" arrow disableHoverListener={!activeFov?.name}>
                  <span className="hs-thumb-stream-name">{activeFov?.name || "Unknown Stream"}</span>
                </Tooltip>
              </div>
              <Body size="small">
                {activeEntry?.timestamp
                  ? moment(new Date(activeEntry.timestamp)).format("MMM DD, YYYY h:mm a")
                  : "N/A"}
              </Body>
            </div>
            {activeLocationText && (
              <Tooltip title={`Location: ${activeLocationText}`} placement="bottom-start" arrow slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, 2] } }] } }}>
                <div className="hs-thumb-location-text">
                  <strong>Location:</strong> {activeLocationText}
                </div>
              </Tooltip>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default SearchResultLightbox;
